import React, { useRef, useState } from "react";
import moment from "moment-timezone";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Api from "../../api";
import Select from "../Select";
import { showNotification } from "../../utils";
import { parseApiDateTime } from "../../utils/datetimeFormat";
import { CLASS_TYPES, LANGUAGES } from "../../consts";
import "react-notifications-component/dist/theme.css";
import "./editar_clase.css";

const BOGOTA_TZ = "America/Bogota";
const DELETED_ZOOM_KEY = "Deleted";

const MODALITY_PRESENCIAL = "presencial";
const MODALITY_VIRTUAL = "virtual";

const LOCK_STARTED = "started";
const LOCK_CLOSED = "closed";

const MSG = {
  invalidId: "Ingresa un ID de clase numérico válido",
  notFound: "Clase no encontrada",
  loadFailed: "No se pudo cargar la clase",
  started: "La clase ya inició y no puede modificarse",
  closed:
    "La clase ya no se puede editar porque su asistencia ya fue cerrada o la clase fue eliminada",
  noBooks: "Selecciona al menos un libro",
  booksLeftEmpty:
    "Estás dejando la clase sin libros. Selecciona al menos un libro antes de guardar.",
  booksEmptyNotice:
    "La clase debe tener al menos un libro. No se puede guardar con la lista vacía.",
  capacityBelowEnrolled: "La capacidad no puede ser menor a los inscritos",
  capacityZero: "La capacidad debe ser mayor a 0",
  noSede: "Selecciona una sede para la modalidad presencial",
  noZoom: "Selecciona una clave de Zoom para la modalidad virtual",
  noZoomKeys: "No hay claves de Zoom disponibles para este horario",
  pendingAction: "Confirma o cancela la acción pendiente antes de guardar",
  zoomConflict:
    "La clave de Zoom seleccionada ya está ocupada en ese horario. Selecciona otra clave.",
  forbidden: "No tienes permiso para modificar esta clase",
  saveFailed: "No se pudo guardar la clase",
  saved: "Clase actualizada",
  noChanges: "No hay cambios para guardar",
  studentRemoved: "Estudiante retirado de la clase",
  studentNotEnrolled: "El estudiante ya no se encuentra inscrito en esta clase",
  removeFailed: "No se pudo retirar al estudiante de la clase",
};

// startDate is the naive "YYYY-MM-DDTHH:mm:ss" string from GET /class/{id},
// expressed in Bogotá wall-clock time.
const hasClassStarted = (startDate) =>
  moment.tz(startDate, BOGOTA_TZ).valueOf() <= Date.now();

const isZoomConflict = (result) =>
  result.status === 409 && /zoom key/i.test(result.message || "");

const sameIdSet = (a, b) => {
  const setA = new Set(a);
  const setB = new Set(b);
  if (setA.size !== setB.size) return false;
  for (const id of setA) {
    if (!setB.has(id)) return false;
  }
  return true;
};

const labelFrom = (options, value) => {
  const match = options.find((o) => o.value === value);
  return match ? match.label : value || "-";
};

const buildSnapshot = (data) => ({
  startDate: data.startDate,
  endDate: data.endDate,
  language: data.language,
  type: data.type,
  teacherName: data.teacherName,
  locationId: data.locationId != null ? Number(data.locationId) : null,
  locationName: data.locationName || "",
  bookIds: (data.bookIds || []).map(Number),
  capacity: Number(data.capacity ?? 0),
});

const draftFromSnapshot = (snapshot) => ({
  bookIds: [...snapshot.bookIds],
  capacity: snapshot.capacity,
  modality:
    snapshot.locationId != null ? MODALITY_PRESENCIAL : MODALITY_VIRTUAL,
  locationId: snapshot.locationId,
  zoomKey: snapshot.locationId == null ? snapshot.locationName : "",
});

const zoomKeysFrom = (result) => {
  if (!result.ok || !Array.isArray(result.data)) return [];
  return result.data
    .map((z) => (z && typeof z.key === "string" ? z.key : ""))
    .filter((key) => key !== "" && key !== DELETED_ZOOM_KEY);
};

const EditarClase = () => {
  const [searchId, setSearchId] = useState("");
  const [loading, setLoading] = useState(false);

  const [classId, setClassId] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [draft, setDraft] = useState(null);
  const [assistance, setAssistance] = useState([]);

  const [bookOptions, setBookOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [zoomCatalog, setZoomCatalog] = useState([]);

  const [lockReason, setLockReason] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [pendingSede, setPendingSede] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  // Save confirmation (Teo, 2026-10-26): { body, changes } shown before PATCH.
  const [pendingSave, setPendingSave] = useState(null);

  // Guards against double clicks while a write is in flight.
  const inFlightRef = useRef(false);
  // Ignores responses from an older search.
  const searchSeqRef = useRef(0);

  const isReadOnly = lockReason !== null;
  const busy = saving || deleting;
  // While the save confirmation is open the draft is frozen, so what the admin
  // reviews is exactly what gets sent.
  const editLocked = isReadOnly || busy || pendingSave !== null;

  const closePendingConfirms = () => {
    setPendingSede(null);
    setPendingDelete(null);
    setPendingSave(null);
  };

  const lock = (reason) => {
    setLockReason(reason);
    closePendingConfirms();
  };

  const resetLoadedState = () => {
    setClassId(null);
    setSnapshot(null);
    setDraft(null);
    setAssistance([]);
    setBookOptions([]);
    setLocationOptions([]);
    setZoomCatalog([]);
    setLockReason(null);
    closePendingConfirms();
  };

  // --- Load ---------------------------------------------------------------

  const handleSearch = async () => {
    if (loading || busy) return;

    const trimmedId = searchId.trim();
    if (!/^\d+$/.test(trimmedId)) {
      showNotification("Aviso", MSG.invalidId, "warning");
      return;
    }

    const seq = searchSeqRef.current + 1;
    searchSeqRef.current = seq;
    setLoading(true);
    resetLoadedState();

    try {
      const [detailRes, assistanceRes] = await Promise.all([
        Api.ClassEdit.GetDetail(trimmedId),
        Api.ClassEdit.GetAssistance(trimmedId),
      ]);
      if (seq !== searchSeqRef.current) return;

      if (!detailRes.ok || !detailRes.data) {
        showNotification(
          "Error",
          detailRes.status === 404 ? MSG.notFound : MSG.loadFailed,
          "danger"
        );
        return;
      }
      if (!assistanceRes.ok) {
        showNotification(
          "Error",
          assistanceRes.status === 404 ? MSG.notFound : MSG.loadFailed,
          "danger"
        );
        return;
      }

      const nextSnapshot = buildSnapshot(detailRes.data);
      const started = hasClassStarted(nextSnapshot.startDate);

      const [booksRes, locationsRes, zoomRes] = await Promise.all([
        Api.ClassEdit.GetBooks(nextSnapshot.language),
        Api.ClassEdit.GetLocations(),
        started
          ? Promise.resolve({ ok: false, status: 0 })
          : Api.ClassEdit.GetZoomKeys(nextSnapshot.startDate),
      ]);
      if (seq !== searchSeqRef.current) return;

      if (
        !booksRes.ok ||
        !Array.isArray(booksRes.data) ||
        !locationsRes.ok ||
        !Array.isArray(locationsRes.data)
      ) {
        showNotification("Error", MSG.loadFailed, "danger");
        return;
      }

      setBookOptions(
        booksRes.data.map((b) => ({ value: Number(b.id), label: b.name }))
      );
      setLocationOptions(
        locationsRes.data.map((l) => ({
          value: Number(l.id),
          label: l.address,
        }))
      );
      setZoomCatalog(zoomKeysFrom(zoomRes));
      setAssistance(Array.isArray(assistanceRes.data) ? assistanceRes.data : []);
      setSnapshot(nextSnapshot);
      setDraft(draftFromSnapshot(nextSnapshot));
      setClassId(trimmedId);
      setLockReason(started ? LOCK_STARTED : null);
    } finally {
      if (seq === searchSeqRef.current) setLoading(false);
    }
  };

  const reloadZoom = async (startDate) => {
    const zoomRes = await Api.ClassEdit.GetZoomKeys(startDate);
    setZoomCatalog(zoomKeysFrom(zoomRes));
  };

  const reloadAssistance = async (id) => {
    const res = await Api.ClassEdit.GetAssistance(id);
    if (!res.ok) {
      showNotification(
        "Error",
        res.status === 404 ? MSG.notFound : MSG.loadFailed,
        "danger"
      );
      return;
    }
    setAssistance(Array.isArray(res.data) ? res.data : []);
  };

  // After a successful PATCH: class, assistance, and zoom (only if still future).
  const reloadAfterSave = async (id) => {
    const [detailRes, assistanceRes] = await Promise.all([
      Api.ClassEdit.GetDetail(id),
      Api.ClassEdit.GetAssistance(id),
    ]);

    if (!detailRes.ok || !detailRes.data || !assistanceRes.ok) {
      const status = !detailRes.ok ? detailRes.status : assistanceRes.status;
      showNotification(
        "Error",
        status === 404 ? MSG.notFound : MSG.loadFailed,
        "danger"
      );
      return;
    }

    const nextSnapshot = buildSnapshot(detailRes.data);
    const started = hasClassStarted(nextSnapshot.startDate);
    if (!started) {
      await reloadZoom(nextSnapshot.startDate);
    }

    setAssistance(Array.isArray(assistanceRes.data) ? assistanceRes.data : []);
    setSnapshot(nextSnapshot);
    setDraft(draftFromSnapshot(nextSnapshot));
    closePendingConfirms();
    if (started) lock(LOCK_STARTED);
  };

  // --- Derived values -----------------------------------------------------

  const capacityFloor = Math.max(1, assistance.length);

  const zoomOptions = (() => {
    if (!snapshot) return [];
    const keys = [...zoomCatalog];
    const loadedVirtual = snapshot.locationId == null;
    if (
      loadedVirtual &&
      snapshot.locationName &&
      snapshot.locationName !== DELETED_ZOOM_KEY &&
      !keys.includes(snapshot.locationName)
    ) {
      keys.push(snapshot.locationName);
    }
    return keys.map((key) => ({ value: key, label: key }));
  })();

  const sedeLabel = (id) => {
    const match = locationOptions.find((o) => o.value === id);
    if (match) return match.label;
    if (snapshot && id === snapshot.locationId) return snapshot.locationName;
    return "-";
  };

  const bookLabel = (id) => {
    const match = bookOptions.find((o) => o.value === id);
    return match ? match.label : `#${id}`;
  };

  const describeBooks = (ids) =>
    ids.length > 0 ? ids.map(bookLabel).join(", ") : "Sin libros";

  const describeModality = (modality, locationId, zoomKey) =>
    modality === MODALITY_PRESENCIAL
      ? `Presencial — ${sedeLabel(locationId)}`
      : `Virtual — ${zoomKey || "-"}`;

  const selectedBookOptions = draft
    ? bookOptions.filter((o) => draft.bookIds.includes(o.value))
    : [];

  const selectedSedeOption =
    draft && draft.locationId != null
      ? { value: draft.locationId, label: sedeLabel(draft.locationId) }
      : null;

  const selectedZoomOption =
    draft && draft.zoomKey ? { value: draft.zoomKey, label: draft.zoomKey } : null;

  // --- Draft editing ------------------------------------------------------

  const handleBooksChange = (selected) => {
    if (editLocked) return;
    const ids = (selected || []).map((o) => Number(o.value));
    // Warn the moment the admin removes the last book (Teo, 2026-10-26).
    if (ids.length === 0 && draft.bookIds.length > 0) {
      showNotification("Aviso", MSG.booksLeftEmpty, "warning");
    }
    setDraft((prev) => ({ ...prev, bookIds: ids }));
  };

  const handleCapacityMinus = () => {
    if (editLocked) return;
    setDraft((prev) =>
      prev.capacity <= capacityFloor
        ? prev
        : { ...prev, capacity: prev.capacity - 1 }
    );
  };

  const handleCapacityPlus = () => {
    if (editLocked) return;
    setDraft((prev) => ({
      ...prev,
      capacity:
        prev.capacity < capacityFloor ? capacityFloor : prev.capacity + 1,
    }));
  };

  const handleModalityChange = (modality) => {
    if (editLocked || !draft || draft.modality === modality) return;
    setPendingSede(null);
    setDraft((prev) => ({
      ...prev,
      modality,
      // Presencial always restarts from the loaded sede so a later sede pick
      // still goes through the sede-to-sede confirm.
      locationId:
        modality === MODALITY_PRESENCIAL ? snapshot.locationId : prev.locationId,
      zoomKey:
        modality === MODALITY_VIRTUAL
          ? snapshot.locationId == null
            ? snapshot.locationName
            : ""
          : prev.zoomKey,
    }));
  };

  const handleSedeChange = (option) => {
    if (editLocked || !option) return;
    const nextId = Number(option.value);
    if (nextId === draft.locationId) return;

    if (snapshot.locationId != null) {
      setPendingDelete(null);
      setPendingSede({ id: nextId, label: option.label });
      return;
    }
    setDraft((prev) => ({ ...prev, locationId: nextId }));
  };

  const handleSedeConfirm = () => {
    if (!pendingSede || isReadOnly || busy) return;
    const nextId = pendingSede.id;
    setDraft((prev) => ({ ...prev, locationId: nextId }));
    setPendingSede(null);
  };

  const handleSedeCancel = () => {
    setPendingSede(null);
  };

  const handleZoomChange = (option) => {
    if (editLocked || !option) return;
    setDraft((prev) => ({ ...prev, zoomKey: option.value }));
  };

  const handleCancel = () => {
    if (editLocked || !snapshot) return;
    setDraft(draftFromSnapshot(snapshot));
    closePendingConfirms();
  };

  // --- Save ---------------------------------------------------------------

  const handleSave = () => {
    if (inFlightRef.current || editLocked || !draft || !snapshot) return;

    if (pendingSede || pendingDelete) {
      showNotification("Aviso", MSG.pendingAction, "warning");
      return;
    }

    if (hasClassStarted(snapshot.startDate)) {
      lock(LOCK_STARTED);
      showNotification("Aviso", MSG.started, "warning");
      return;
    }

    if (draft.bookIds.length === 0) {
      showNotification("Aviso", MSG.noBooks, "warning");
      return;
    }

    const capacityChanged = draft.capacity !== snapshot.capacity;
    if (capacityChanged && draft.capacity < capacityFloor) {
      showNotification("Aviso", MSG.capacityBelowEnrolled, "warning");
      return;
    }

    if (
      draft.modality === MODALITY_PRESENCIAL &&
      (draft.locationId == null || Number.isNaN(Number(draft.locationId)))
    ) {
      showNotification("Aviso", MSG.noSede, "warning");
      return;
    }
    if (
      draft.modality === MODALITY_VIRTUAL &&
      !(typeof draft.zoomKey === "string" && draft.zoomKey.trim() !== "")
    ) {
      showNotification("Aviso", MSG.noZoom, "warning");
      return;
    }

    const body = {};
    if (!sameIdSet(draft.bookIds, snapshot.bookIds)) {
      body.books = draft.bookIds.map(Number);
    }
    if (capacityChanged) {
      body.capacity = draft.capacity;
    }

    const loadedPresencial = snapshot.locationId != null;
    if (draft.modality === MODALITY_PRESENCIAL) {
      if (!loadedPresencial || draft.locationId !== snapshot.locationId) {
        body.location_id = Number(draft.locationId);
      }
    } else if (loadedPresencial || draft.zoomKey !== snapshot.locationName) {
      body.zoom_key = draft.zoomKey;
    }

    if (Object.keys(body).length === 0) {
      showNotification("Aviso", MSG.noChanges, "info");
      return;
    }

    // Human-readable list of what will change, shown in the save confirmation.
    const changes = [];
    if (body.books) {
      changes.push({
        label: "Libros",
        from: describeBooks(snapshot.bookIds),
        to: describeBooks(draft.bookIds),
      });
    }
    if (body.capacity !== undefined) {
      changes.push({
        label: "Cupo",
        from: String(snapshot.capacity),
        to: String(draft.capacity),
      });
    }
    if (body.location_id !== undefined || body.zoom_key !== undefined) {
      changes.push({
        label: "Modalidad",
        from: describeModality(
          loadedPresencial ? MODALITY_PRESENCIAL : MODALITY_VIRTUAL,
          snapshot.locationId,
          snapshot.locationName
        ),
        to: describeModality(draft.modality, draft.locationId, draft.zoomKey),
      });
    }

    setPendingSave({ body, changes });
  };

  const handleSaveCancel = () => {
    setPendingSave(null);
  };

  const handleSaveConfirm = async () => {
    if (inFlightRef.current || isReadOnly || busy || !pendingSave) return;

    // Re-check right before PATCH: time may have passed while the panel was open.
    if (hasClassStarted(snapshot.startDate)) {
      lock(LOCK_STARTED);
      showNotification("Aviso", MSG.started, "warning");
      return;
    }

    const { body } = pendingSave;
    inFlightRef.current = true;
    setSaving(true);
    try {
      const result = await Api.ClassEdit.Update(classId, body);
      setPendingSave(null);

      if (result.ok) {
        showNotification("Éxito", MSG.saved, "success");
        await reloadAfterSave(classId);
        return;
      }

      if (isZoomConflict(result)) {
        showNotification("Aviso", MSG.zoomConflict, "warning");
        await reloadZoom(snapshot.startDate);
        return;
      }

      switch (result.status) {
        case 409:
          lock(LOCK_CLOSED);
          showNotification("Aviso", MSG.closed, "warning");
          break;
        case 400:
          showNotification("Aviso", MSG.capacityBelowEnrolled, "warning");
          break;
        case 422:
          showNotification("Aviso", MSG.capacityZero, "warning");
          break;
        case 404:
          showNotification("Error", MSG.notFound, "danger");
          break;
        case 401:
        case 403:
          showNotification("Error", MSG.forbidden, "danger");
          break;
        default:
          showNotification("Error", MSG.saveFailed, "danger");
      }
    } finally {
      inFlightRef.current = false;
      setSaving(false);
    }
  };

  // --- Remove student -----------------------------------------------------

  const handleAskDelete = (student) => {
    if (editLocked) return;
    setPendingSede(null);
    setPendingDelete(student);
  };

  const handleDeleteCancel = () => {
    setPendingDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (inFlightRef.current || isReadOnly || busy || !pendingDelete) return;

    if (hasClassStarted(snapshot.startDate)) {
      lock(LOCK_STARTED);
      showNotification("Aviso", MSG.started, "warning");
      return;
    }

    const student = pendingDelete;
    inFlightRef.current = true;
    setDeleting(true);
    try {
      const result = await Api.ClassEdit.RemoveStudent(
        classId,
        student.customer_id
      );

      if (result.ok) {
        setPendingDelete(null);
        showNotification("Éxito", MSG.studentRemoved, "success");
        await reloadAssistance(classId);
        return;
      }

      if (result.status === 404) {
        setPendingDelete(null);
        showNotification("Aviso", MSG.studentNotEnrolled, "warning");
        await reloadAssistance(classId);
        return;
      }

      if (result.status === 409 && !isZoomConflict(result)) {
        lock(LOCK_CLOSED);
        showNotification("Aviso", MSG.closed, "warning");
        return;
      }

      if (result.status === 401 || result.status === 403) {
        showNotification("Error", MSG.forbidden, "danger");
        return;
      }

      showNotification("Error", MSG.removeFailed, "danger");
    } finally {
      inFlightRef.current = false;
      setDeleting(false);
    }
  };

  // --- Render -------------------------------------------------------------

  const renderConfirmPanel = (content, onCancel, onConfirm) => (
    <div className="editar-clase-confirm">
      {typeof content === "string" ? <p>{content}</p> : content}
      <div className="editar-clase-confirm-actions">
        <button
          type="button"
          className="editar-clase-btn editar-clase-btn-secondary"
          onClick={onCancel}
          disabled={busy}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="editar-clase-btn"
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? "Procesando..." : "Confirmar"}
        </button>
      </div>
    </div>
  );

  const startParts = snapshot ? parseApiDateTime(snapshot.startDate) : null;
  const endParts = snapshot ? parseApiDateTime(snapshot.endDate) : null;

  return (
    <div className="editar-clase-container">
      <div className="editar-clase-search">
        <label htmlFor="editar-clase-id">ID de la clase:</label>
        <input
          id="editar-clase-id"
          type="text"
          value={searchId}
          onChange={(e) => setSearchId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearch();
          }}
          placeholder="Ej: 43017"
          disabled={loading || busy}
        />
        <button
          type="button"
          className="editar-clase-btn"
          onClick={handleSearch}
          disabled={loading || busy}
        >
          {loading ? "Buscando..." : "Buscar"}
        </button>
      </div>

      {loading && <div className="editar-clase-loading">Cargando clase...</div>}

      {!loading && !snapshot && (
        <div className="editar-clase-placeholder">
          Ingresa el ID de la clase que quieres modificar
        </div>
      )}

      {!loading && snapshot && draft && (
        <div className="editar-clase-card">
          <div className="editar-clase-card-header">
            <h3>Clase — ID: {classId}</h3>
          </div>

          {isReadOnly && (
            <div className="editar-clase-banner">
              {lockReason === LOCK_STARTED ? MSG.started : MSG.closed}
            </div>
          )}

          <div className="editar-clase-info">
            <div className="editar-clase-info-item">
              <span>Fecha inicio</span>
              <span>{startParts.date}</span>
            </div>
            <div className="editar-clase-info-item">
              <span>Hora inicio</span>
              <span>{startParts.time}</span>
            </div>
            <div className="editar-clase-info-item">
              <span>Fecha fin</span>
              <span>{endParts.date}</span>
            </div>
            <div className="editar-clase-info-item">
              <span>Hora fin</span>
              <span>{endParts.time}</span>
            </div>
            <div className="editar-clase-info-item">
              <span>Idioma</span>
              <span>{labelFrom(LANGUAGES, snapshot.language)}</span>
            </div>
            <div className="editar-clase-info-item">
              <span>Tipo</span>
              <span>{labelFrom(CLASS_TYPES, snapshot.type)}</span>
            </div>
            <div className="editar-clase-info-item">
              <span>Profesor</span>
              <span>{snapshot.teacherName || "-"}</span>
            </div>
          </div>

          <div className="editar-clase-section">
            <h4>Libros</h4>
            <div className="editar-clase-books">
              <Select
                name="editar-clase-books"
                isMulti
                options={bookOptions}
                value={selectedBookOptions}
                onChange={handleBooksChange}
                placeholder="Selecciona los libros"
                disabled={editLocked}
              />
            </div>
            {!isReadOnly && draft.bookIds.length === 0 && (
              <p className="editar-clase-notice">{MSG.booksEmptyNotice}</p>
            )}
          </div>

          <div className="editar-clase-section">
            <h4>Cupo</h4>
            <div className="editar-clase-capacity">
              <button
                type="button"
                className="editar-clase-stepper"
                onClick={handleCapacityMinus}
                disabled={editLocked || draft.capacity <= capacityFloor}
                aria-label="Disminuir cupo"
              >
                −
              </button>
              <span className="editar-clase-capacity-value">
                {draft.capacity}
              </span>
              <button
                type="button"
                className="editar-clase-stepper"
                onClick={handleCapacityPlus}
                disabled={editLocked}
                aria-label="Aumentar cupo"
              >
                +
              </button>
              <span className="editar-clase-capacity-info">
                Inscritos: {assistance.length} / {draft.capacity}
              </span>
            </div>
          </div>

          <div className="editar-clase-section">
            <h4>Modalidad</h4>
            <div className="editar-clase-modality">
              <button
                type="button"
                className={`editar-clase-toggle${
                  draft.modality === MODALITY_PRESENCIAL ? " active" : ""
                }`}
                onClick={() => handleModalityChange(MODALITY_PRESENCIAL)}
                disabled={editLocked}
              >
                Presencial
              </button>
              <button
                type="button"
                className={`editar-clase-toggle${
                  draft.modality === MODALITY_VIRTUAL ? " active" : ""
                }`}
                onClick={() => handleModalityChange(MODALITY_VIRTUAL)}
                disabled={editLocked}
              >
                Virtual
              </button>
            </div>

            {draft.modality === MODALITY_PRESENCIAL ? (
              <div className="editar-clase-modality-select">
                <label>Sede</label>
                <Select
                  name="editar-clase-sede"
                  options={locationOptions}
                  value={selectedSedeOption}
                  onChange={handleSedeChange}
                  placeholder="Selecciona una sede"
                  disabled={editLocked}
                />
              </div>
            ) : (
              <div className="editar-clase-modality-select">
                <label>Clave de Zoom</label>
                <Select
                  name="editar-clase-zoom"
                  options={zoomOptions}
                  value={selectedZoomOption}
                  onChange={handleZoomChange}
                  placeholder="Selecciona una clave de Zoom"
                  disabled={editLocked}
                />
                {zoomOptions.length === 0 && (
                  <p className="editar-clase-notice">{MSG.noZoomKeys}</p>
                )}
              </div>
            )}

            {pendingSede &&
              !isReadOnly &&
              renderConfirmPanel(
                `¿Confirmas el cambio de sede? La clase dejará ${sedeLabel(
                  draft.locationId
                )} y quedará en ${pendingSede.label}.`,
                handleSedeCancel,
                handleSedeConfirm
              )}
          </div>

          <div className="editar-clase-section">
            <h4>Estudiantes inscritos</h4>
            {assistance.length > 0 ? (
              <table className="editar-clase-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>ID</th>
                    <th>Libro</th>
                    <th>Sesión</th>
                    {!isReadOnly && <th aria-label="Acciones" />}
                  </tr>
                </thead>
                <tbody>
                  {assistance.map((student) => (
                    <tr key={student.customer_id}>
                      <td>{student.full_name || "-"}</td>
                      <td>{student.customer_id}</td>
                      <td>{student.book_name || "-"}</td>
                      <td>{student.session_name || "-"}</td>
                      {!isReadOnly && (
                        <td className="editar-clase-table-action">
                          <button
                            type="button"
                            className="editar-clase-trash"
                            onClick={() => handleAskDelete(student)}
                            disabled={editLocked}
                            title="Retirar estudiante de la clase"
                            aria-label="Retirar estudiante de la clase"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="editar-clase-empty">No hay estudiantes inscritos</p>
            )}

            {pendingDelete &&
              !isReadOnly &&
              renderConfirmPanel(
                `¿Confirmas retirar a ${
                  pendingDelete.full_name || pendingDelete.customer_id
                } (${pendingDelete.customer_id})? El estudiante saldrá de esta clase y la clase se revertirá en su pace.`,
                handleDeleteCancel,
                handleDeleteConfirm
              )}
          </div>

          {pendingSave &&
            !isReadOnly &&
            renderConfirmPanel(
              <div className="editar-clase-save-summary">
                <p>¿Confirmas guardar estos cambios en la clase {classId}?</p>
                <ul>
                  {pendingSave.changes.map((change) => (
                    <li key={change.label}>
                      <strong>{change.label}:</strong> {change.from} →{" "}
                      {change.to}
                    </li>
                  ))}
                </ul>
              </div>,
              handleSaveCancel,
              handleSaveConfirm
            )}

          {!isReadOnly && (
            <div className="editar-clase-actions">
              <button
                type="button"
                className="editar-clase-btn"
                onClick={handleSave}
                disabled={editLocked}
              >
                {saving ? "Guardando..." : "Guardar Cambios"}
              </button>
              <button
                type="button"
                className="editar-clase-btn editar-clase-btn-secondary"
                onClick={handleCancel}
                disabled={editLocked}
              >
                Cancelar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EditarClase;
